import React from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Calendar, Clock, DollarSign, CheckCircle, ExternalLink, Sparkles, Zap } from "lucide-react";
import { motion } from "framer-motion";

export default function Bookings() {
  const features = [
    "One-on-one personalized sessions",
    "Expert peer tutors",
    "All subjects available",
    "Affordable pricing",
    "Exam preparation support",
    "Study skills development"
  ];

  const steps = [
    {
      number: 1,
      title: "Fill Out the Form",
      description: "Tell us about your learning needs and schedule preferences",
      color: "from-blue-500 to-cyan-500",
      darkColor: "dark:from-red-500 dark:to-orange-500"
    },
    {
      number: 2,
      title: "Get Matched",
      description: "Choose your perfect tutor for your subject and goals.",
      color: "from-purple-500 to-pink-500",
      darkColor: "dark:from-orange-500 dark:to-amber-500"
    },
    {
      number: 3,
      title: "Start Learning",
      description: "Begin your personalized tutoring sessions and watch your confidence grow",
      color: "from-green-500 to-emerald-500",
      darkColor: "dark:from-amber-500 dark:to-yellow-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:bg-gradient-to-b dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-950 transition-colors duration-300">
      {/* Limited Time Offer Banner */}
      <motion.div
        className="w-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 dark:bg-gradient-to-r dark:from-red-600 dark:via-orange-600 dark:to-amber-600 py-4 px-4 shadow-xl"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-3 text-center flex-wrap">
            <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-yellow-300 dark:text-yellow-400 animate-pulse flex-shrink-0" />
            <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
              <span className="text-lg md:text-2xl font-black text-white">
                🎉 SIBLING DISCOUNT!
              </span>
              <span className="text-sm md:text-base font-bold text-white/95">
                Enroll first child for $65/hr • Additional siblings 10% off!
              </span>
            </div>
            <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-yellow-300 dark:text-yellow-400 animate-pulse flex-shrink-0" />
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-20 dark:opacity-30">
          <div className="absolute top-20 left-0 w-96 h-96 bg-green-300 dark:bg-red-600/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-0 w-96 h-96 bg-blue-300 dark:bg-orange-500/30 rounded-full blur-3xl animate-pulse delay-700"></div>
        </div>

        {/* Floating shapes */}
        <motion.div
          animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-32 right-20 hidden lg:block"
        >
          <div className="w-20 h-20 bg-yellow-300 dark:bg-amber-500/40 rounded-2xl opacity-30 dark:opacity-40" />
        </motion.div>

        <motion.div
          animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-40 left-20 hidden lg:block"
        >
          <div className="w-16 h-16 bg-blue-300 dark:bg-red-500/30 rounded-full opacity-30 dark:opacity-40" />
        </motion.div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative py-12 md:py-20">
{/* Header */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h1 className="text-5xl md:text-7xl font-extrabold bg-gradient-to-r from-green-900 via-blue-900 to-purple-900 dark:bg-gradient-to-r dark:from-white dark:via-red-400 dark:to-orange-400 bg-clip-text text-transparent mb-6 pb-2">
              Start Your Learning Journey
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-zinc-300 max-w-3xl mx-auto leading-relaxed">
              Schedule a session with one of our <span className="font-bold text-blue-600 dark:text-red-400">elite tutors</span> and experience the difference peer-to-peer learning makes.
            </p>
          </motion.div>

          {/* NEW BUTTON ADDED HERE */}
          <motion.div 
            className="flex justify-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <a 
              href="/bookingsform" 
              className="block w-full max-w-md"
            >
              <Button 
                size="lg"
                className="w-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 dark:bg-gradient-to-r dark:from-red-600 dark:via-orange-600 dark:to-amber-600 hover:from-blue-700 hover:via-purple-700 hover:to-pink-700 dark:hover:from-red-700 dark:hover:via-orange-700 dark:hover:to-amber-700 text-white shadow-2xl hover:shadow-3xl transition-all duration-300 group relative overflow-hidden"
              >
                {/* Animated background */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                
                <div className="relative flex items-center justify-center gap-3">
                  <Calendar className="w-6 h-6 text-yellow-300 dark:text-yellow-400" />
                  <span className="text-xl font-bold">Book Your Session Now</span>
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <ExternalLink className="w-5 h-5" />
                  </motion.div>
                </div>
              </Button>
            </a>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Pricing Card */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className="p-8 border-3 border-blue-200 dark:border-red-500/40 bg-white dark:bg-zinc-900/90 backdrop-blur-sm shadow-2xl relative overflow-hidden group hover:shadow-3xl transition-all duration-300 h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 dark:bg-gradient-to-br dark:from-zinc-900/50 dark:to-zinc-800/50 opacity-50"></div>
                <div className="relative">
                  <div className="text-center mb-8">
                    <motion.div 
                      className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 dark:from-red-500 dark:to-orange-600 rounded-3xl mb-4 shadow-xl"
                      whileHover={{ rotate: 360, scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    >
                      <DollarSign className="w-10 h-10 text-white" />
                    </motion.div>
                    <h2 className="text-4xl font-black text-gray-900 dark:text-white mb-2">Simple Pricing</h2>
                    <p className="text-gray-600 dark:text-zinc-400 font-medium">No hidden fees, no commitments</p>
                  </div>

                  <motion.div 
                    className="text-center mb-8 p-10 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 dark:bg-gradient-to-br dark:from-red-500 dark:via-orange-500 dark:to-amber-500 rounded-3xl shadow-2xl relative overflow-hidden"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="absolute inset-0 bg-white/10 dark:bg-white/5 backdrop-blur-sm"></div>
                    <div className="relative">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Sparkles className="w-8 h-8 text-yellow-300 dark:text-red-400" />
                        <div className="text-7xl font-black text-white">$65</div>
                        <Sparkles className="w-8 h-8 text-yellow-300 dark:text-red-400" />
                      </div>
                      <div className="text-2xl text-white font-bold">per hour</div>
                    </div>
                  </motion.div>

                  <div className="space-y-3">
                    {features.map((feature, idx) => (
                      <motion.div
                        key={idx}
                        className="flex items-center gap-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:bg-gradient-to-r dark:from-zinc-900/70 dark:to-zinc-800/70 p-4 rounded-xl border border-green-200 dark:border-red-500/30"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + idx * 0.1 }}
                        whileHover={{ x: 5 }}
                      >
                        <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-500 dark:from-red-500 dark:to-orange-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-gray-800 dark:text-zinc-300 font-semibold">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* Right Column: Bundle Info & How It Works in vertical layout */}
            <div className="space-y-8">
              {/* How It Works Card - BOTTOM RIGHT */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
              >
                <Card className="p-8 border-3 border-purple-200 dark:border-red-500/40 bg-white dark:bg-zinc-900/90 backdrop-blur-sm shadow-2xl relative overflow-hidden hover:shadow-3xl transition-all duration-300">
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-pink-50 dark:bg-gradient-to-br dark:from-zinc-900/50 dark:to-zinc-800/50 opacity-50"></div>
                  <div className="relative">
                    <div className="text-center mb-8">
                      <motion.div 
                        className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 dark:from-red-500 dark:to-orange-600 rounded-3xl mb-4 shadow-xl"
                        whileHover={{ rotate: 360, scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                      >
                        <Clock className="w-10 h-10 text-white" />
                      </motion.div>
                      <h2 className="text-4xl font-black text-gray-900 dark:text-white mb-2">How It Works</h2>
                      <p className="text-gray-600 dark:text-zinc-400 font-medium">Getting started is easy</p>
                    </div>

                    <div className="space-y-6">
                      {steps.map((step, index) => (
                        <motion.div
                          key={index}
                          className="flex gap-5 relative"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.7 + index * 0.15 }}
                        >
                          {index < steps.length - 1 && (
                            <div className="absolute left-6 top-14 bottom-0 w-1 bg-gradient-to-b from-gray-300 to-transparent dark:from-red-500/40 dark:to-transparent"></div>
                          )}
                          <motion.div 
                            className={`w-14 h-14 bg-gradient-to-br ${step.color} ${step.darkColor} rounded-2xl flex items-center justify-center flex-shrink-0 font-black text-white text-xl shadow-xl relative z-10`}
                            whileHover={{ scale: 1.1, rotate: 5 }}
                          >
                            {step.number}
                          </motion.div>
                          <div>
                            <h3 className="font-bold text-xl text-gray-900 dark:text-white mb-2">{step.title}</h3>
                            <p className="text-gray-600 dark:text-zinc-300 leading-relaxed">{step.description}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </Card>
              </motion.div>

                            {/* Bundle Container - TOP RIGHT */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Card className="border-3 border-amber-200 dark:border-amber-500/40 bg-gradient-to-br from-amber-50 to-orange-50 dark:bg-gradient-to-br dark:from-zinc-900/90 dark:to-zinc-800/90 backdrop-blur-sm shadow-2xl relative overflow-hidden hover:shadow-3xl transition-all duration-300 h-full">
                  <div className="p-8 h-full flex flex-col">
                    {/* Header */}
                    <div className="mb-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 dark:from-amber-600 dark:to-orange-700 rounded-xl flex items-center justify-center shadow-lg">
                          <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <span className="text-sm font-bold text-amber-700 dark:text-amber-400 tracking-wide">SPECIAL BUNDLE OFFER</span>
                          <h3 className="text-2xl font-black text-gray-900 dark:text-white">
                            Bundle: 3 Lessons — $165
                          </h3>
                        </div>
                      </div>
                    </div>

                    {/* Content grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-1">
                      {/* Left side - Benefits */}
                      <div>
                        <h4 className="font-bold text-gray-800 dark:text-zinc-300 mb-3 text-lg">Benefits:</h4>
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0" />
                            <span className="text-gray-700 dark:text-zinc-300">Save $30 total</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 flex-shrink-0" />
                            <span className="text-gray-700 dark:text-zinc-300">$55 per hour</span>
                          </div>
                        </div>
                      </div>

                      {/* Right side - Pricing */}
                      <div className="flex flex-col items-center justify-center">
                        <div className="mb-4 text-center">
                          <div className="text-gray-600 dark:text-zinc-400 font-medium mb-1">Regular Price</div>
                          <div className="text-xl text-gray-500 dark:text-zinc-500 line-through font-bold">$195</div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 dark:bg-gradient-to-br dark:from-amber-600 dark:via-orange-600 dark:to-red-600 rounded-xl p-5 shadow-xl w-full max-w-xs">
                          <div className="text-center">
                            <div className="text-white text-xs font-bold mb-1 tracking-wide">BUNDLE PRICE</div>
                            <div className="text-3xl font-black text-white">$165</div>
                            <div className="text-white/90 text-sm font-semibold">for 3 lessons</div>
                          </div>
                        </div>
                        
                        <p className="mt-4 text-sm text-gray-600 dark:text-zinc-400 text-center">
                          <span className="font-bold text-amber-600 dark:text-amber-500">Limited spots available</span>
                        </p>
                      </div>
                    </div>

                    {/* Accent line */}
                    <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 dark:from-amber-600 dark:via-orange-600 dark:to-red-600"></div>
                  </div>
                </Card>
              </motion.div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <Card className="p-10 md:p-16 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 dark:bg-gradient-to-br dark:from-red-500 dark:via-orange-500 dark:to-amber-500 border-none shadow-2xl text-white text-center relative overflow-hidden">
              {/* Animated background */}
              <div className="absolute inset-0 opacity-30 dark:opacity-40">
                <div className="absolute top-10 left-10 w-40 h-40 bg-yellow-400 dark:bg-red-600/50 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-10 right-10 w-60 h-60 bg-pink-400 dark:bg-orange-500/50 rounded-full blur-3xl animate-pulse delay-700"></div>
              </div>

              <div className="relative">
                <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
                  <Zap className="w-12 h-12 text-yellow-300 dark:text-red-400" />
                  <h2 className="text-4xl md:text-6xl font-black">
                    Ready to Transform Your Learning?
                  </h2>
                  <Sparkles className="w-12 h-12 text-yellow-300 dark:text-red-400" />
                </div>
                <p className="text-xl md:text-2xl text-blue-100 dark:text-zinc-200 mb-10 max-w-3xl mx-auto leading-relaxed font-medium">
                  Fill out our booking form and get connected with the <span className="font-bold text-yellow-300 dark:text-red-300">perfect tutor</span> for your needs.
                </p>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <a 
                    href="/bookingsform" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button size="lg" className="bg-gradient-to-r from-yellow-400 to-orange-500 dark:bg-gradient-to-r dark:from-red-500 dark:to-orange-600 hover:from-yellow-500 hover:to-orange-600 dark:hover:from-red-600 dark:hover:to-orange-700 text-gray-900 dark:text-white shadow-2xl group text-xl font-black px-12 py-8 rounded-2xl">
                      Book Your First Session Now
                      <ExternalLink className="w-6 h-6 ml-3 group-hover:translate-x-2 transition-transform" />
                    </Button>
                  </a>
                </motion.div>
                <motion.p 
                  className="mt-8 text-blue-100 dark:text-zinc-300 text-lg"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                >
                  <CheckCircle className="w-6 h-6 inline mr-2 text-green-300 dark:text-orange-400" />
                  Quick response
                </motion.p>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}